import moment from 'moment';
import i18n from '@/i18n';

const t = (message) => {
    return i18n.global.t(message);
};

const buildDependencies = (quantities) => {
    quantities.forEach((x) => {
        x.allRequired = getRequired(quantities, x.quantityId);
    });

    quantities.forEach((x) => {
        x.requires = getRequires(quantities, x.quantityId);
    });
};

const deselect = (all, quantities, quantityId, message, assign) => {
    const newQuantities = [...quantities];
    let others = [];
    if (assign) {
        others = all.find((x) => x.quantityId === quantityId).requires;
    } else {
        others = all.find((x) => x.quantityId === quantityId).allRequired;
    }

    if (others.length > 0 && others.some((x) => quantities.includes(x))) {
        const missing = others.filter((x) => quantities.includes(x));
        if (
            confirm(
                message.replace(
                    /\[x]/g,
                    '\n' + missing.map((x) => all.find((y) => y.quantityId === x).name).join('\n'),
                ),
            )
        ) {
            newQuantities.splice(newQuantities.indexOf(quantityId), 1);
            missing.forEach((x) => newQuantities.splice(newQuantities.indexOf(x), 1));
        }
    } else {
        newQuantities.splice(newQuantities.indexOf(quantityId), 1);
    }

    return newQuantities;
};

const formatEndDate = (date) => {
    return date ? moment(date).format('DD/MM/YYYY') : t('No expiry');
};

const formatStartDate = (date) => {
    return date ? moment(date).format('DD/MM/YYYY') : t('Unknown');
};

const formatUsername = (user) => {
    if (user.firstName && user.lastName) {
        return user.firstName + ' ' + user.lastName + ' (' + user.username + ')';
    } else if (user.firstName) {
        return user.firstName + ' (' + user.username + ')';
    } else if (user.lastName) {
        return user.lastName + ' (' + user.username + ')';
    }

    return user.username;
};

const getBlocked = (all, gracePeriod, userLicences, includeImplicit = true) => {
    if (!gracePeriod) {
        const explicit = userLicences
            .filter((x) => {
                const period = all.find((y) => y.quantityId === x.quantityId).minimumPeriod;
                return x.assigned && x.assigned > moment().subtract(period, 'days').format('YYYY-MM-DD HH:mm:ss');
            })
            .map((x) => x.quantityId);
        let implicit = [];
        if (includeImplicit) {
            explicit.forEach((x) => {
                implicit = implicit.concat(
                    all
                        .find((y) => y.quantityId === x)
                        .allRequired.filter((z) => !explicit.includes(z) && !implicit.includes(z)),
                );
            });
        }

        return explicit.concat(implicit);
    }

    return [];
};

const getGroups = (quantities) => {
    const groups = [];
    quantities.forEach((x) => {
        if (!groups.includes(x.group)) {
            groups.push(x.group);
        }
    });
    groups.sort((a, b) => (a > b ? 1 : -1));
    return groups;
};

const getRequired = (all, quantityId) => {
    const quantity = all.find((x) => x.quantityId === quantityId);
    return [...quantity.required].concat(quantity.required.map((x) => getRequired(all, x)).flat());
};

const getRequires = (all, quantityId) => {
    return all.filter((x) => x.allRequired.includes(quantityId)).map((x) => x.quantityId);
};

const getUserLicences = (user, licences) => {
    return user.licences
        .filter((y) => y.licenceId || licences.some((z) => z.id === y.guid))
        .map((y) => {
            const licence = licences.find((z) => z.id === y.guid);
            if (licence) {
                return { ...y, quantityId: licence.quantityId, end: licence.end };
            }

            return { ...y, quantityId: y.licenceId };
        });
};

const loadLicences = (loader, callback) => {
    loader('licencesummaryv1/get', {}, (response) => {
        const quantities = response.quantities.sort((a, b) => (a.name > b.name ? 1 : -1));
        const licences = response.licences.sort((a, b) => (a.end > b.end ? 1 : -1));
        const gracePeriod = response.gracePeriod;
        const useDefinitions = response.useDefinitions;
        quantities.forEach((x) => {
            x.total = -1;
            if (response.licences.some((y) => y.quantityId === x.quantityId && y.quantity) || !useDefinitions) {
                x.total = response.licences
                    .filter((y) => y.quantityId === x.quantityId && y.quantity)
                    .map((y) => y.quantity)
                    .reduce((a, b) => a + b, 0);
            }
            x.assigned = response.licences
                .filter((y) => y.quantityId === x.quantityId)
                .map((y) => y.assigned.length)
                .reduce((a, b) => a + b, 0);
        });
        buildDependencies(quantities);

        callback(quantities, licences, gracePeriod, useDefinitions);
    });
};

const select = (all, quantities, quantityId, message, assign) => {
    const newQuantities = [...quantities];
    let others = [];
    if (assign) {
        others = all.find((x) => x.quantityId === quantityId).allRequired;
    } else {
        others = all.find((x) => x.quantityId === quantityId).requires;
    }

    if (others.length > 0 && others.some((x) => !quantities.includes(x))) {
        const missing = others.filter((x) => !quantities.includes(x));
        if (
            confirm(
                message.replace(
                    /\[x]/g,
                    '\n' + missing.map((x) => all.find((y) => y.quantityId === x).name).join('\n'),
                ),
            )
        ) {
            newQuantities.push(quantityId);
            missing.forEach((x) => newQuantities.push(x));
        }
    } else {
        newQuantities.push(quantityId);
    }

    return newQuantities;
};

export {
    buildDependencies,
    deselect,
    formatEndDate,
    formatStartDate,
    formatUsername,
    getBlocked,
    getGroups,
    getUserLicences,
    loadLicences,
    select,
};
